import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import "../styles/main.css";
import styles from "../styles/licenseList.module.css";
import {
  getAllLicenses,
  getLicensesForHq,
  getLicensePermissions,
  updateLicensePermissionsBulk,
  getManufacturersData,
  getComingSoonManufacturers,
  getTrainingsListWithPermissions,
} from "./helpers/requests";
import { formatFirestoreDate, isFirestoreDateExpired } from "./helpers/helpers";
import ChangeLicenseDataModal from "./modals/changeLicenseDataModal";
import ConfirmDeleteLicenseModal from "./modals/confirmDeleteLicenseModal";
import AddHeadsetModal from "./modals/addHeadsetModal";
import ConfirmDeleteHeadsetModal from "./modals/confirmDeleteHeadsetModal";
import ConfirmUnpairHeadsetModal from "./modals/confirmUnpairHeadsetModal";
import ConfirmHideModal from "./modals/confirmHideModal";
import ConfirmPermissionModal from "./modals/confirmPermissionModal";
import { useAuth } from "../firebase/auth.tsx";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string; // targetId (licenseId)
  permission?: boolean;
}

const LicenseList = ({ licenses, setLicenses }) => {
  const { t } = useTranslation();
  const { headquarterId: routeHeadquarterId } = useParams<{ headquarterId?: string }>();
  const { userRole } = useAuth();
  
  const [selectedLicense, setSelectedLicense] = useState<any>(null);
  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonManufacturers, setComingSoonManufacturers] = useState<any[]>([]);
  const [isLoadingLicenses, setIsLoadingLicenses] = useState(true);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [collapsedManufacturers, setCollapsedManufacturers] = useState<Set<string>>(new Set());
  
  // Modals
  const [showChangeLicenseModal, setShowChangeLicenseModal] = useState(false);
  const [showDeleteLicenseModal, setShowDeleteLicenseModal] = useState(false);
  const [showAddHeadsetModal, setShowAddHeadsetModal] = useState(false);
  const [showUnpairModal, setShowUnpairModal] = useState(false);
  const [showConfirmActionModal, setShowConfirmActionModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] = useState<SelectedTraining | false>(false);
  
  const [pendingAction, setPendingAction] = useState<{
    manufacturerId: string;
    manufacturerName: string;
    actionType: "select" | "hide";
    value: boolean;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter hook
  const {
    filteredData: filteredLicenses,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(licenses || []);

  useEffect(() => {
    fetchData();
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOptionsOpen(false);
      }
    };

    const handleHeadsetAdded = (event: any) => {
        const newHeadset = event.detail;
        if (newHeadset && newHeadset.licenseId) {
            setLicenses((prevLicenses) =>
                prevLicenses.map((license) =>
                    license.id === newHeadset.licenseId
                        ? {
                            ...license,
                            deviceId: newHeadset.deviceId,
                            headsetDocId: newHeadset.id,
                            headsetName: newHeadset.name,
                        }
                        : license
                )
            );

            setSelectedLicense((prev: any) => {
                if (prev && prev.id === newHeadset.licenseId) {
                    return {
                        ...prev,
                        deviceId: newHeadset.deviceId,
                        headsetDocId: newHeadset.id,
                        headsetName: newHeadset.name,
                    };
                }
                return prev;
            });
        }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("headsetAdded" as any, handleHeadsetAdded);

    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("headsetAdded" as any, handleHeadsetAdded);
    };
  }, [routeHeadquarterId]);

  const fetchData = async () => {
    setIsLoadingLicenses(true);
    try {
      let data;
      if (routeHeadquarterId) {
        data = await getLicensesForHq(routeHeadquarterId);
      } else {
        data = await getAllLicenses();
      }
      setLicenses(data);
    } catch (error) {
      console.error("Error fetching licenses:", error);
    } finally {
      setIsLoadingLicenses(false);
    }
  };

  const groupTrainingsByManufacturer = (trainings) => {
    const grouped: any = {};
    trainings.forEach((t: any) => {
        if (!grouped[t.manufacturerId]) {
            grouped[t.manufacturerId] = {
                id: t.manufacturerId,
                name: t.manufacturerName,
                trainings: []
            };
        }
        grouped[t.manufacturerId].trainings.push(t);
    });
    return Object.values(grouped);
  };

  const handleLicenseClick = async (license: any) => {
    setSelectedLicense(license);
    setIsLoadingPermissions(true);
    
    const showAll = !routeHeadquarterId;

    try {
      const [allTrainings, comingSoon] = await Promise.all([
        getTrainingsListWithPermissions(license.id, "license", showAll),
        getComingSoonManufacturers(license.id, "license", showAll)
      ]);

      const enrichedTrainings = groupTrainingsByManufacturer(allTrainings);

      setTrainingsList(enrichedTrainings);
      setComingSoonManufacturers(comingSoon);
      
      const hiddenManufacturers = new Set<string>();
      enrichedTrainings.forEach((m: any) => {
        if (m.trainings.length > 0 && m.trainings.every((t: any) => t.isHidden)) {
           hiddenManufacturers.add(m.id);
        }
      });
      setCollapsedManufacturers(hiddenManufacturers);

    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const handlePermissionChange = async (trainingId: string, targetId: string, newPermissionState: boolean) => {
    if (!selectedLicense) return;

    // Find the current training to get its isHidden state
    let isHidden = false;
    for (const m of trainingsList) {
        const t = m.trainings.find((tr: any) => tr.id === trainingId);
        if (t) {
            isHidden = t.isHidden;
            break;
        }
    }

    try {
      await updateLicensePermissionsBulk(targetId, [{ trainingId, isChecked: newPermissionState, isHidden }]);
      
      setTrainingsList(prevList => 
        prevList.map(manufacturer => ({
          ...manufacturer,
          trainings: manufacturer.trainings.map(training => 
            training.id === trainingId ? { ...training, permission: newPermissionState } : training
          )
        }))
      );
    } catch (error) {
      console.error("Error updating permission:", error);
    }
  };

  const handleSelectAll = (manufacturerId: string, shouldSelect: boolean) => {
    const manufacturer = trainingsList.find(m => m.id === manufacturerId);
    if (!manufacturer) return;

    setPendingAction({
      manufacturerId,
      manufacturerName: manufacturer.name,
      actionType: "select",
      value: shouldSelect
    });
    setShowConfirmActionModal(true);
  };

  const handleHideClick = (manufacturerId: string, shouldHide: boolean) => {
    // Check if it's a regular manufacturer or coming soon
    let manufacturerName = "";
    const regularManufacturer = trainingsList.find(m => m.id === manufacturerId);
    if (regularManufacturer) {
        manufacturerName = regularManufacturer.name;
    } else {
        const comingSoon = comingSoonManufacturers.find(m => m.id === manufacturerId);
        if (comingSoon) {
            manufacturerName = comingSoon.name;
        } else {
            return;
        }
    }

    setPendingAction({
        manufacturerId,
        manufacturerName: manufacturerName,
        actionType: "hide",
        value: shouldHide
    });
    setShowConfirmActionModal(true);
  };

  const confirmBulkAction = async () => {
    if (!pendingAction || !selectedLicense) return;

    const { manufacturerId, actionType, value } = pendingAction;
    setShowConfirmActionModal(false);
    setPendingAction(null);

    const isComingSoon = comingSoonManufacturers.some(m => m.id === manufacturerId);

    if (isComingSoon) {
        const originalComingSoon = [...comingSoonManufacturers];
        setComingSoonManufacturers(prev => prev.map(m => 
            m.id === manufacturerId ? { ...m, isHidden: value } : m
        ));

        if (actionType === "hide") {
            try {
                await updateLicensePermissionsBulk(selectedLicense.id, [{ trainingId: manufacturerId, isChecked: true, isHidden: value }]);
            } catch (error) {
                console.error("Error updating coming soon permission:", error);
                setComingSoonManufacturers(originalComingSoon);
            }
        }
    } else {
        const manufacturer = trainingsList.find(m => m.id === manufacturerId);
        if (manufacturer) {
            const originalTrainingsList = [...trainingsList];

            setTrainingsList(prevList => 
                prevList.map(m => m.id === manufacturerId ? {
                    ...m,
                    trainings: m.trainings.map((t: any) => ({
                        ...t,
                        permission: actionType === "select" ? value : t.permission,
                        isHidden: actionType === "hide" ? value : t.isHidden
                    }))
                } : m)
            );

            if (actionType === "hide" && value === true) {
                setCollapsedManufacturers(prev => new Set(prev).add(manufacturerId));
            }

            try {
                // Use bulk update API to prevent request flooding
                const updates = manufacturer.trainings.map((t: any) => ({
                    trainingId: t.id,
                    isHidden: actionType === "hide" ? value : t.isHidden,
                    isChecked: actionType === "select" ? value : t.permission
                }));

                await updateLicensePermissionsBulk(selectedLicense.id, updates);

            } catch (error) {
                console.error("Error in bulk update:", error);
                setTrainingsList(originalTrainingsList);
            }
        }
    }
  };

  const toggleCollapse = (manufacturerId: string) => {
    setCollapsedManufacturers(prev => {
      const next = new Set(prev);
      if (next.has(manufacturerId)) next.delete(manufacturerId);
      else next.add(manufacturerId);
      return next;
    });
  };

  const renderOptionsDropdown = () => {
    if (!selectedLicense) return null;

    return (
      <div className={styles.dropdownMenu} ref={dropdownRef}>
        {userRole === "admin" && !routeHeadquarterId && (
            <button onClick={() => setShowChangeLicenseModal(true)}>
            <img src="images/Icon_Edit.png" alt="Edit" />
            {t("Edit_License")}
            </button>
        )}
        {!selectedLicense.deviceId && (
          <button onClick={() => setShowAddHeadsetModal(true)}>
            <img src="images/Icon_Add.png" alt="Pair" />
            {t("Pair_Headset")}
          </button>
        )}
        {selectedLicense.deviceId && (
          <button onClick={() => setShowUnpairModal(true)}>
            <img src="images/Icon_Delete.png" alt="Unpair" />
            {t("Unpair_Headset")}
          </button>
        )}
        {userRole === "admin" && !routeHeadquarterId && (
          <button onClick={() => setShowDeleteLicenseModal(true)}>
            <img src="images/Icon_Delete.png" alt="Delete" />
            {t("Delete_License")}
          </button>
        )}
      </div>
    );
  };

  const renderConfirmPermissionModal = () => {
    if (typeof showConfirmPermissionModal === 'boolean' && !showConfirmPermissionModal) return null;

    const { id, name, entityId, permission } = showConfirmPermissionModal as SelectedTraining;

    return (
      <ConfirmPermissionModal
        setShowConfirmPermissionModal={() => setShowConfirmPermissionModal(false)}
        trainingId={id}
        trainingName={name}
        targetId={entityId}
        targetName={selectedLicense.headsetName || selectedLicense.id}
        permission={permission}
        handlePermissionChange={handlePermissionChange}
      />
    );
  };

  return (
    <div className={styles.adminContainer}>
      <div className={styles.leftContainer}>
        <div className={styles.tableHeader}>
          <table className="table table-borderless">
            <thead>
              <tr>
                <th style={{ width: "30%" }}>{t("License")}</th>
                <th style={{ width: "30%" }}>{t("Headset")}</th>
                <th style={{ width: "30%" }}>
                  <TableHeaderFilter
                    column="headquarterName"
                    title={t("Headquarter")}
                    data={licenses || []}
                    filterPanels={filterPanels}
                    filters={filters}
                    searchQueries={searchQueries}
                    panelRefs={panelRefs}
                    toggleFilterPanel={toggleFilterPanel}
                    handleFilterChange={handleFilterChange}
                    handleFilterSearch={handleFilterSearch}
                    setFilters={setFilters}
                  />
                </th>
                <th style={{ width: "10%" }} className={styles.editButtonWrapper}></th>
              </tr>
            </thead>
          </table>
        </div>
        <div className={styles.tableContent}>
          {isLoadingLicenses ? (
            <div className={styles.loading}>{t("Loading")}...</div>
          ) : (
            <div className={`container-fluid ${styles.containerFluid}`}>
            <table className="table table-borderless">
              <tbody>
                {filteredLicenses.map((license, index) => (
                  <tr 
                    key={license.id} 
                    style={{
                        backgroundColor:
                          index % 2 === 0 ? "#D4E5E2" : "transparent",
                    }}
                  >
                    <td style={{ width: "30%" }}>
                      <div className={styles.licenseInfoWrapper}>
                        {license.id}
                        <div className={styles.licenseData}>
                            <span className={`${styles.licenseDate} ${isFirestoreDateExpired(license.expirationDate) ? styles.expiredDate : ""}`}>{t("Expiration_Date")}: {formatFirestoreDate(license.expirationDate, t)}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ width: "30%" }}>
                        {license.headsetName || license.deviceId || "-"}
                    </td>
                    <td style={{ width: "30%" }}>{license.headquarterName}</td>
                    <td style={{ width: "10%" }} className={styles.editButtonWrapper}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleLicenseClick(license)}
                            style={{
                              backgroundColor: selectedLicense?.id === license.id ? "#404040" : "#39A686",
                              borderColor: selectedLicense?.id === license.id ? "#404040" : "#39A686",
                            }}
                          >
                            {t("View")}
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <div className={styles.rightContainer}>
        {selectedLicense ? (
          <>
            <div className={styles.containerHeaderWrapper}>
              <div className={styles.containerHeader}>
                <div className={styles.userDataWrapper}>
                  <div className={styles.userDataName}>
                    {selectedLicense.headsetName || selectedLicense.id}
                  </div>
                  <div className={`${styles.userDataEmail} ${isFirestoreDateExpired(selectedLicense.expirationDate) ? styles.expiredDate : ""}`}>{t("Expiration_Date")}: {formatFirestoreDate(selectedLicense.expirationDate, t)}</div>
                </div>
              </div>

              <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}>
                  <div className="dropdown" ref={dropdownRef}>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#39A686",
                          borderColor: "#39A686",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                    </button>
                    {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                            {userRole === "admin" && !routeHeadquarterId && (
                                <button onClick={() => {setShowChangeLicenseModal(true); setIsOptionsOpen(false);}}>
                                <img src="/images/Icon_Edit.png" alt="Edit" />
                                {t("Edit_License")}
                                </button>
                            )}
                            {!selectedLicense.deviceId && (
                            <button onClick={() => {setShowAddHeadsetModal(true); setIsOptionsOpen(false);}}>
                                <img src="/images/Icon_Add.png" alt="Pair" />
                                {t("Pair_Headset")}
                            </button>
                            )}
                            {selectedLicense.deviceId && (
                            <button onClick={() => {setShowUnpairModal(true); setIsOptionsOpen(false);}}>
                                <img src="/images/Icon_Delete.png" alt="Unpair" />
                                {t("Unpair_Headset")}
                            </button>
                            )}
                            {userRole === "admin" && !routeHeadquarterId && (
                            <button onClick={() => {setShowDeleteLicenseModal(true); setIsOptionsOpen(false);}}>
                                <img src="/images/Icon_Delete.png" alt="Delete" />
                                {t("Delete_License")}
                            </button>
                            )}
                        </div>
                    )}
                  </div>
              </div>
              <hr />
              <p className={styles.containerHeaderTitle}>{t("Training_permission")}</p>
              <p className={styles.containerHeaderDescription}>
                {t("Manage_trainings_for_this_license")}
              </p>
            </div>

            <div className={`${styles.trainingsContainer} thin-scrollbar`}>
              {isLoadingPermissions ? (
                <p className={styles.loading}>{t("Loading")}...</p>
              ) : (
                <>
                {trainingsList.map((manufacturer) => {
                    const isManufacturerHidden = manufacturer.trainings.every((t: any) => t.isHidden);
                    const isCollapsed = collapsedManufacturers.has(manufacturer.id);
                    const allSelected = manufacturer.trainings.every((t: any) => t.permission);

                    return (
                  <div key={manufacturer.id} className={styles.trainingsGroup}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {!isManufacturerHidden && (
                      <span 
                        onClick={() => toggleCollapse(manufacturer.id)}
                        style={{ cursor: "pointer", marginRight: "10px", fontSize: "0.8em", userSelect: "none" }}
                      >
                          {isCollapsed ? "\u25B6" : "\u25BC"}
                      </span>
                      )}
                      <label 
                        className={styles.trainingsGroupTitle}
                        style={{ color: isManufacturerHidden ? "gray" : "#39a686", cursor: "pointer", marginBottom: 0 }}
                        onClick={() => !isManufacturerHidden && toggleCollapse(manufacturer.id)}
                      >
                        {manufacturer.name}
                      </label>
                      
                      {!collapsedManufacturers.has(manufacturer.id) && !isManufacturerHidden && (
                         <span 
                            onClick={() => handleSelectAll(manufacturer.id, !allSelected)}
                            style={{
                                marginLeft: "1vh",
                                marginTop: "0.4vh",
                                fontSize: "0.7em",
                                cursor: "pointer",
                                color: "#212529"
                            }}
                         >
                            {allSelected ? t("Unselect_all") : t("Select_all")}
                         </span>
                      )}
                      
                      {!routeHeadquarterId && (
                        <span 
                            onClick={() => handleHideClick(manufacturer.id, !isManufacturerHidden)}
                            style={{
                                marginLeft: "1vh",
                                marginTop: "0.4vh",
                                fontSize: "0.7em",
                                cursor: "pointer",
                                color: "#212529"
                            }}
                        >
                            {isManufacturerHidden ? t("Show") : t("Hide")}
                        </span>
                      )}
                    </div>
                    <div className={`${styles.collapsibleWrapper} ${collapsedManufacturers.has(manufacturer.id) ? styles.collapsed : ""}`}>
                      <div className={styles.collapsibleInner}>
                        <ul className="list-unstyled">
                          {manufacturer.trainings.map((training: any) => (
                            <li key={training.id} className="form-check custom-checkbox">
                                <label className="custom-checkbox-label">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={training.permission}
                                    onChange={() => setShowConfirmPermissionModal({
                                        id: training.id,
                                        name: training.name,
                                        entityId: selectedLicense.id,
                                        permission: training.permission,
                                    })}
                                    id={`training-${training.id}`}
                                    disabled={training.isHidden}
                                />
                                <span className="checkmark"></span>
                                <span className="form-check-label" style={{ color: training.isHidden ? "gray" : "#212529" }}>
                                    {training.name}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )})}
                {comingSoonManufacturers.length > 0 && (
                    <label
                        className={styles.trainingsGroupTitle}
                        style={{ color: "#39a686", marginTop: "20px", display: "block" }}
                    >
                        {t("Coming_soon")}:
                    </label>
                )}
                {comingSoonManufacturers.map(({ id, name, isHidden }) => {
                    return (
                        <div key={id} className={styles.trainingsGroup}>
                        <div className={styles.trainingsGroupTitle} style={{ display: 'flex', alignItems: 'center' }}>
                            <span
                                style={{ color: isHidden ? "gray" : "#212529" }}
                            >
                                {name}
                            </span>
                            
                                                            {!routeHeadquarterId && (
                                                                <span
                                                                    onClick={() => handleHideClick(id, !isHidden)}
                                                                    style={{
                                                                        marginLeft: "1vh",
                                                                        fontSize: "0.7em",
                                                                        cursor: "pointer",
                                                                        color: "#212529"
                                                                    }}
                                                                >
                                                                    {isHidden ? t("show") : t("hide")}
                                                                </span>
                                                            )}                            
                        </div>
                        </div>
                    );
                })}
                </>
              )}
            </div>
          </>
        ) : (
          <p className={styles.selectUser}>{t("Select_license_to_view_permissions")}</p>
        )}
      </div>

      {/* Modals */}
      {showChangeLicenseModal && selectedLicense && (
        <ChangeLicenseDataModal
          setShowChangeLicenseDataModal={setShowChangeLicenseModal}
          license={selectedLicense}
          handleChangeLicenseData={(updated) => {
            setLicenses(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } : l));
            
            if (selectedLicense && selectedLicense.headquarterId !== updated.headquarterId) {
                const newLicenseData = { ...selectedLicense, ...updated };
                setSelectedLicense(newLicenseData);
                handleLicenseClick(newLicenseData);
            } else {
                setSelectedLicense(prev => ({ ...prev, ...updated }));
            }
          }}
          headquarterId={selectedLicense.headquarterId}
        />
      )}

      {showDeleteLicenseModal && selectedLicense && (
        <ConfirmDeleteLicenseModal
          setShowConfirmDeleteLicenseModal={setShowDeleteLicenseModal}
          licenseId={selectedLicense.id}
          handleDeleteLicense={() => {
            setLicenses(prev => prev.filter(l => l.id !== selectedLicense.id));
            setSelectedLicense(null);
            setShowDeleteLicenseModal(false);
          }}
        />
      )}

      {showAddHeadsetModal && selectedLicense && (
        <AddHeadsetModal
          setShowAddHeadsetModal={setShowAddHeadsetModal}
          headquarterId={selectedLicense.headquarterId}
          licenseId={selectedLicense.id}
        />
      )}

      {showUnpairModal && selectedLicense && (
        <ConfirmUnpairHeadsetModal
          setShowConfirmUnpairHeadsetModal={setShowUnpairModal}
          licenseId={selectedLicense.id}
          headsetName={selectedLicense.headsetName}
          handleUnpairHeadsetSubmit={() => {
            const updated = { ...selectedLicense, deviceId: null, headsetName: "", headsetDocId: null };
            setLicenses(prev => prev.map(l => l.id === selectedLicense.id ? updated : l));
            setSelectedLicense(updated);
          }}
        />
      )}

      {showConfirmActionModal && pendingAction && (
        <ConfirmHideModal
          setShowConfirmHideModal={setShowConfirmActionModal}
          manufacturerId={pendingAction.manufacturerId}
          manufacturerName={pendingAction.manufacturerName}
          handleHideClick={confirmBulkAction}
          title={
              pendingAction.actionType === "hide"
              ? (pendingAction.value ? t("Hide") : t("Show"))
              : (pendingAction.value ? t("Select_all") : t("Unselect_all"))
          }
          description={
              pendingAction.actionType === "hide"
              ? (pendingAction.value ? t("Are_you_sure_you_want_to_hide_this_manufacturer") : t("Are_you_sure_you_want_to_show_this_manufacturer"))
              : (pendingAction.value 
                ? t("Are_you_sure_you_want_to_select_all_trainings_for_this_manufacturer")
                : t("Are_you_sure_you_want_to_unselect_all_trainings_for_this_manufacturer"))
          }
          confirmText={t("Confirm")}
          cancelText={t("Cancel")}
        />
      )}

      {showConfirmPermissionModal && renderConfirmPermissionModal()}
    </div>
  );
};

export default LicenseList;